import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  MenuProps,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import { VisuallyHiddenInput } from 'components/common/utils'
import DownloadIcon from '@mui/icons-material/Download'
import ClearIcon from '@mui/icons-material/Clear'
import { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import { useATIGraphFilterData } from 'apis/ActionTracker/InisightGraphFilterData'
import { AxiosResponse } from 'axios'
import LinearProgressWithLabel from 'components/common/LinearProgressWithLabel'
import LoadsheetValidation from 'components/Loadsheet/LoadsheetValidation'
import { BytesToSize } from 'utils/utils'
import HelpIcon from '@mui/icons-material/Help'
import VideoModal from 'components/common/VideoModal'
import { useHotkeys } from 'react-hotkeys-hook'
import { KEYBOARD_SHORTCUTS } from 'config/keyboardShortcuts'
import { APIErrorMessage } from 'utils/apiUtils'
import { useModulePermission } from 'hooks/useModulePermission'
import { filterPermitedAssets } from 'components/ActionManagement/actionTrackerCommon'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const helpVideoUrl = 'https://aie-public.s3.eu-west-1.amazonaws.com/oe/action_tracker_loadsheet.mp4'
const menuProps: Partial<MenuProps> = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 9 + ITEM_PADDING_TOP,
      overflowX: 'hidden',
    },
    className: 'custom-scroll',
  },
  // variant: 'selectedMenu',
}

type LoadsheetComponentProps = {
  FileDownload: () => Promise<AxiosResponse<Blob>>
  FileUpload: (formdata: FormData) => Promise<AxiosResponse<Blob>>
  LoadsheetColumns: any
  filters: any
  setFilters: React.Dispatch<React.SetStateAction<any>>
  disableDownload: boolean
  disableUpload: boolean
  uploadPercentage?: number
  isUploading?: boolean
  maxSize?: number
  cancelUpload?: () => void
}

const LoadsheetComponent: React.FC<LoadsheetComponentProps> = ({
  FileDownload,
  FileUpload,
  LoadsheetColumns,
  filters,
  setFilters,
  uploadPercentage,
  isUploading,
  maxSize,
  cancelUpload,
  disableDownload,
  disableUpload,
}) => {
  const { project_id } = useParams<{ project_id: string }>()
  const [dsbleButton, setDsbleButton] = useState(false)
  const inputFileRef = useRef<HTMLInputElement>(null)
  const [showhelpVideo, setShowHelpVideo] = useState(false)
  const [{ file, file_name }, setFileData] = useState<{ file_name: string; file: string | Blob }>({
    file_name: 'Select location',
    file: '',
  })
  const [validationData, setValidateData] = useState<any>(null)
  const [errorText, setErrorText] = useState('')
  const { permissions } = useModulePermission()

  const {
    data: filterApiData, // for drowdowns
    isLoading: isFilterDataLoading,
    refetch: filterRefetch,
  } = useATIGraphFilterData({
    project_id,
    config: { cacheTime: 1000 },
  })

  const filteredAssets = useMemo(() => {
    return filterPermitedAssets(
      filterApiData?.assets,
      permissions,
      ['BatchCreateUpdateAssessmentFormAction', 'BatchDownloadAssessmentFormAction']
    )
  }, [filterApiData?.assets, permissions?.subprojects])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const acceptedFormats = ['.zip', '.xlsx']
    if (files) {
      const file = files[0]

      if (file) {
        if (!acceptedFormats.some((format) => file.name?.toLowerCase().endsWith(format))) {
          toast.error('Unsupported File Type!!')
          setErrorText('Your Upload must be in .zip or .xlsx format')
          setFileData({ file_name: 'Select location', file: '' })
          return
        }
        if (maxSize && file?.size > maxSize) {
          toast.error(`File size exceeds the maximum limit of ${BytesToSize(maxSize)}`)
          return
        }
        setFileData({ file_name: file.name, file: file })
        setErrorText('')
        setValidateData(null)
        if (inputFileRef?.current) {
          inputFileRef.current.value = ''
        }
      }
    } else {
      setFileData({ file_name: 'Select location', file: '' })
      setErrorText('Please select a file')
      setValidateData(null)
      if (inputFileRef?.current) {
        inputFileRef.current.value = ''
      }
    }
  }

  const handleDeleteFile = () => {
    setFileData({ file_name: 'Select location', file: '' })
    setValidateData(null)
    if (inputFileRef?.current != null) {
      inputFileRef.current.value = ''
    }
    cancelUpload && cancelUpload()
  }

  const handleUploadLoadsheet = (validateExcel = true) => {
    setDsbleButton(true)
    if (validationData?.is_valid === false) {
      toast.error('Not a valid file')
      return
    }

    const formData = new FormData()

    const is_cached_expired = validationData?.cached_file_expiry
      ? validationData?.cached_file_expiry < Date.now() / 1000
      : false

    if (!validationData?.cached_filename || is_cached_expired) {
      formData.append('file', file)
      formData.append('file_name', file_name)
    }

    formData.append('validate', validateExcel.toString())
    formData.append('cached_zip_filename', validationData?.cached_filename || '')
    FileUpload(formData)
      .then((response: any) => {
        if (validateExcel) {
          setValidateData(response?.data)
        } else {
          setValidateData(null)
          toast.success('Loadsheet uploaded successfully')
          setFileData({ file_name: 'Select location', file: '' })
          if (inputFileRef?.current != null) {
            inputFileRef.current.value = ''
          }
        }
      })
      .catch((error) => {
        APIErrorMessage(error)
      })
      .finally(() => {
        setDsbleButton(false)
      })
  }

  const downloadLoadsheet = async () => {
    if (dsbleButton || !filters?.asset_id) return
    setDsbleButton(true)
    await FileDownload()
      .then((response: any) => {
        const blob = new Blob([response?.data as Blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const fileName = response?.headers['content-disposition']?.split('filename=')[1]
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        link.click()
      })
      .catch((error) => {
        APIErrorMessage(error)
      })
      .finally(() => {
        setDsbleButton(false)
      })
  }

  const getUploadProgessTooltip = () => {
    const uploadedBytes = (uploadPercentage! / 100) * (file as Blob).size
    const uploadedSize = BytesToSize(uploadedBytes, 2, true)
    return `${uploadPercentage}% Uploaded (${uploadedSize})`
  }

  useHotkeys(KEYBOARD_SHORTCUTS.DOWNLOAD_PRIMARY.keys, () => downloadLoadsheet())

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={4}
        sx={{ padding: 1, width: '100%', marginTop: '5px' }}
        id="loadsheet-setup-section"
      >
        <Box padding={2} maxWidth={700}>
          <Typography display={'block'} variant="h2DMSansBold" className="color-veracity">
            Download Loadsheet
          </Typography>
          <Typography variant="RobotoBody" display={'block'} paddingTop={1} paddingBottom={2}>
            Make sure you are downloading the correct loadsheet. When filling in the loadsheet do
            not make any changes to the table structure as this will be automatically rejected by
            the system.
          </Typography>
          <Box display={'flex'} gap={2} alignItems={'center'}>
            <FormControl style={{ width: 300 }} sx={{ marginTop: 0 }} size="small">
              <InputLabel id={`asset-label`} className="ati-quick-filters-multi-select">
                Asset
              </InputLabel>
              <Select
                variant="outlined"
                value={filters?.asset_id}
                labelId={`asset-label`}
                label="Asset"
                MenuProps={menuProps}
                onChange={(e) => {
                  setFilters((prev: any) => ({ ...prev, asset_id: e.target.value }))
                }}
              >
                {filteredAssets?.map((asset: any) => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {asset.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              sx={{ height: 40, backgroundColor: '#1B2064', textTransform: 'none' }}
              disabled={dsbleButton || !filters?.asset_id || isUploading || disableDownload}
              className="button-primary-1"
              onClick={downloadLoadsheet}
            >
              <DownloadIcon style={{ fontSize: 20, marginRight: 5 }} />
              Download Loadsheet
            </Button>
            <Tooltip title="Watch video for help on how to fill the loadsheet">
              <IconButton onClick={() => setShowHelpVideo(true)} sx={{ marginLeft: -2 }}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box padding={2} className="loadsheet-setup-upload-div">
          <Typography display={'block'} variant="DmSanssectionHeading">
            Upload Loadsheet
          </Typography>
          <Typography
            variant="DmSanssectionSubHeading"
            display={'inline-block'}
            paddingTop={1}
            paddingBottom={2}
          >
            Your Upload must be in .zip format.
          </Typography>
          {maxSize && (
            <Typography
              variant="DmSanssectionSubHeading"
              display={'inline-block'}
              sx={{ marginLeft: 1 }}
            >
              Max Allowed File Size {BytesToSize(maxSize, 0, true)}
            </Typography>
          )}
          <Box display={'flex'} gap={0} sx={{ height: 50 }} alignItems={'center'} paddingTop={0}>
            <Button
              component="label"
              variant="outlined"
              className="loadsheet-upload-button"
              disabled={isUploading || disableUpload}
              tabIndex={-1}
              sx={{
                color: !!errorText ? 'red' : '#675E55',
                border: !!errorText ? '2px solid red' : '2px solid #b0adac',
                textTransform: 'none',
                height: 50,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileChange}
                disabled={isUploading || disableUpload}
                ref={inputFileRef}
                accept=".zip, application/zip, application/x-zip-compressed, application/x-zip, application/octet-stream, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />{' '}
              Choose Folder
            </Button>
            <Box
              sx={{
                color: !!errorText ? 'red' : '#675E55',
                border: !!errorText ? '2px solid red' : '2px solid #b0adac',
                textTransform: 'none',
                height: 50,
                width: 400,
                borderTopRightRadius: 5,
                borderBottomRightRadius: 5,
                marginLeft: '-2px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '5px',
                borderLeft: 'none',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="DmSanssectionSubHeading"
                sx={{
                  color: !!errorText ? 'red' : '#675E55',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {file_name}
              </Typography>
              {file && (
                <ClearIcon
                  sx={{ fontSize: 18, marginRight: 1, cursor: 'pointer' }}
                  onClick={handleDeleteFile}
                />
              )}
            </Box>
            <Button
              sx={{
                height: 40,
                marginLeft: '10px',
                backgroundColor: '#1B2064',
                textTransform: 'none',
              }}
              variant="contained"
              disabled={
                dsbleButton ||
                !!validationData ||
                !file_name ||
                !filters?.asset_id ||
                isUploading ||
                disableUpload
              }
              onClick={() => handleUploadLoadsheet(true)}
            >
              Upload
            </Button>
          </Box>
          {!!errorText && (
            <Box>
              <Typography
                variant="DmSanssectionSubHeading"
                sx={{ color: !!errorText ? 'red' : '' }}
              >
                {' '}
                {errorText}{' '}
              </Typography>
            </Box>
          )}
        </Box>
        {isUploading && (
          <Tooltip title={getUploadProgessTooltip()}>
            <Box component="span">
              <LinearProgressWithLabel
                variant="determinate"
                value={uploadPercentage ?? 0}
                disablePercentage
              />
            </Box>
          </Tooltip>
        )}
      </Paper>
      {LoadsheetColumns !== null && validationData !== null && (
        <Paper
          elevation={4}
          sx={{ padding: 1, margin: 0, marginTop: 3 }}
          id="loadsheet-setup-section"
        >
          <LoadsheetValidation
            validationData={validationData?.validation}
            columnData={LoadsheetColumns}
            file={file}
          />
          <Button
            variant="contained"
            onClick={() => {
              handleUploadLoadsheet(false)
            }}
            disabled={dsbleButton || !file || validationData?.is_valid == false}
            sx={{
              height: 40,
              float: 'right',
              marginTop: 3,
              marginRight: 2,
              marginBottom: 1,
            }}
            className="button-primary-1"
          >
            Submit
          </Button>
        </Paper>
      )}
      <VideoModal
        isOpen={showhelpVideo}
        onClose={() => setShowHelpVideo(false)}
        url={helpVideoUrl}
        fileName="OE bulk upload demo video.mp4"
      />
    </Box>
  )
}

export default LoadsheetComponent
